// Feature: M004-F0702 (WebUI #52, Pending half). Tests: T-M004-0180, T-M004-0182.
// Design authority: docs/design/M015-F0032-Teacher-Ledger-Page.md §1 R4.
//
// Pure model for the teacher's own pending list. Core serves the rows
// (`GET /v1/orgs/:orgId/ledger/users/me/pending`) with the billing pipeline's
// own `stage` and `state`; this module only words them. It computes NO price,
// no total and no state of its own: an amount is printed from the served
// `amount_minor` + `currency` and nothing else (price plans are Core-only).

import { VIEW_LABELS, formatMinorAmount, formatItemWhen } from '../bookings-admin/billingPipelineModel';
import { STAGE_INLINE } from '../ledgerMoves/stageInline';

// The admin Billing tab groups pipeline states into four views whose labels are
// VIEW_LABELS (Core viewForState: checking/returned -> need, approved -> ok,
// posted/handed_over/charged -> billed, dispatch_failed -> prob). The teacher
// sees the same words for the same states. Only own-pending states are listed:
// `charged` (billed) and `checked:rejected` are never served and never shown.
const STATE_WORDS = {
  previewed: 'Previewed',
  checking: VIEW_LABELS.need,
  'checked:returned': `${VIEW_LABELS.need} (returned)`,
  'checked:approved': VIEW_LABELS.ok,
  // M004-F0716 (I2/I3): Core's `posted` means Core dispatched it and the Ledger has NOT acknowledged it (acknowledged = handed_over).
  // That is stage `sending_to_ledger`, so the words come from the ONE shared stage map (ledgerMoves/stageInline), never a second copy.
  posted: STAGE_INLINE.sending_to_ledger({}),
  handed_over: 'Handed over to the Ledger',
  dispatch_failed: VIEW_LABELS.prob,
};

const STAGE_WORDS = { preview: 'Preview', check: 'Check', ledger: 'Ledger' };

// Core never serves these; a row carrying one is a contract breach and is not
// shown as "pending" (defence in depth, not a filter Core relies on).
const NOT_PENDING_STATES = new Set(['charged', 'checked:rejected']);

export const stateWords = (state) => STATE_WORDS[state] || String(state || 'Unknown');
export const stageWords = (stage) => STAGE_WORDS[stage] || String(stage || 'Unknown');

export const pendingDescription = (item) => [item?.booking_title, item?.room, item?.plan]
  .filter((part) => typeof part === 'string' && part.trim()).join(' · ');

export const pendingWhen = formatItemWhen;

// "amount if present": nothing is printed when Core sent no amount.
export const pendingAmount = (item) => (
  item?.amount_minor === undefined || item?.amount_minor === null ? '' : formatMinorAmount(item.amount_minor, item.currency)
);

// GET .../users/me/pending -> { success, data: { items, total, limit } }
export const normalisePending = (response) => {
  const body = response?.data?.data || response?.data || {};
  const served = Array.isArray(body.items) ? body.items : [];
  const items = served.filter((item) => item && !NOT_PENDING_STATES.has(item.state));
  return {
    items,
    total: Number.isFinite(Number(body.total)) ? Number(body.total) : items.length,
    limit: Number.isFinite(Number(body.limit)) ? Number(body.limit) : items.length,
  };
};

export const PENDING_PAGE = 100;
export const PENDING_MAX = 200;
// "Show more" asks Core for a bigger `limit` (no offset in the contract); at the
// 200 cap the page says how many are not shown instead of pretending.
export const nextLimit = (limit) => Math.min(PENDING_MAX, limit + PENDING_PAGE);
